import React from "react";
import { Box, Button, MenuItem, TextField, Alert } from "@mui/material";
import SectionCard from "./SectionCard";

// ✅ cast select value types (boolean/number) so payload sends true/false not "true"
function castSelectValue(raw, opts) {
  if (!opts || opts.length === 0) return raw;

  const hasBool = opts.some((o) => typeof o.value === "boolean");
  if (hasBool) {
    if (raw === "true") return true;
    if (raw === "false") return false;
  }

  const hasNumber = opts.some((o) => typeof o.value === "number");
  if (hasNumber && raw !== "" && !Number.isNaN(Number(raw))) return Number(raw);

  return raw;
}

function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  return String(v).trim() === "";
}

function isValidEmail(v) {
  const s = String(v || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function isValidPhone(v) {
  const s = String(v || "").trim();
  // Accept 10 digits or +<countrycode> + 10 digits
  // examples: 9876543210 , +919876543210
  return /^(\+\d{1,3})?\d{10}$/.test(s);
}

export default function EntityForm({
  title,
  fields = [],
  initialValues = {},
  onSubmit,
  loading,
}) {
  const [values, setValues] = React.useState(initialValues);
  const [err, setErr] = React.useState("");
  const [ok, setOk] = React.useState("");

  React.useEffect(() => {
    setValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialValues)]);

  const getOptions = (f, v) =>
    typeof f.options === "function" ? f.options(v) : f.options || [];

  const setField = (f) => (e) => {
    const raw = e.target.value;

    if (f.type === "select") {
      const opts = getOptions(f, values);
      setValues((p) => ({ ...p, [f.name]: castSelectValue(raw, opts) }));
      return;
    }

    setValues((p) => ({ ...p, [f.name]: raw }));
  };

  const validate = () => {
    for (const f of fields) {
      const v = values?.[f.name];

      // ✅ required
      if (f.required && isEmpty(v)) return `${f.label} is required`;

      // ✅ email/phone validation (when requested)
      if (f.validation === "email" && !isEmpty(v) && !isValidEmail(v)) {
        return `Enter valid email for ${f.label}`;
      }
      if (f.validation === "phone" && !isEmpty(v) && !isValidPhone(v)) {
        return `Enter valid phone for ${f.label} (10 digits or +91XXXXXXXXXX)`;
      }

      // ✅ custom validation hook
      if (typeof f.validate === "function") {
        const msg = f.validate(v, values);
        if (msg) return msg;
      }
    }
    return "";
  };

  const submit = async () => {
    setErr("");
    setOk("");

    const msg = validate();
    if (msg) {
      setErr(msg);
      return;
    }

    try {
      await onSubmit(values, { reset: () => setValues(initialValues) });
      setOk("Saved ✅");
    } catch (e) {
      setErr(
        e?.response?.data ? JSON.stringify(e.response.data) : e?.message || "Failed"
      );
    }
  };

  return (
    <SectionCard title={title}>
      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
      {ok ? <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert> : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {fields.map((f) => {
          const opts = getOptions(f, values);
          const isFull = f.fullRow ? { gridColumn: { xs: "1", md: "1 / span 2" } } : undefined;

          if (f.type === "select") {
            return (
              <TextField
                key={f.name}
                select
                label={f.label}
                value={values?.[f.name] ?? ""}
                onChange={setField(f)}
                fullWidth
                size="small"
                sx={isFull}
              >
                {opts.map((op) => (
                  <MenuItem key={String(op.value)} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </TextField>
            );
          }

          return (
            <TextField
              key={f.name}
              label={f.label}
              value={values?.[f.name] ?? ""}
              onChange={setField(f)}
              placeholder={f.placeholder || ""}
              fullWidth
              size="small"
              sx={isFull}
            />
          );
        })}
      </Box>

      {/* ✅ centered button */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          onClick={submit}
          disabled={loading}
          sx={{ px: 4, py: 1.2, borderRadius: 2, fontWeight: 800 }}
        >
          {loading ? "Saving..." : "Submit"}
        </Button>
      </Box>
    </SectionCard>
  );
}